import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import authService from '../services/authService';
import { addBreadcrumb, logger, setTag } from '../utils/sentryUtils';

// Helper function to map role IDs to role names
const mapRoleIdsToRoleName = (roleIds) => {
  if (!roleIds || roleIds.length === 0) return 'user';
  
  // Map role IDs to role names based on the actual database
  const roleMap = {
    1: 'student',
    2: 'coach', 
    3: 'staff',
    4: 'crew',
    5: 'caregiver',
    6: 'mentor',
    7: 'alumni',
    8: 'support',
    9: 'inactive',
    10: 'stakeholder',
    11: 'team_supervisor'
  };
  
  // Return the highest priority role (lower numbers = higher priority)
  const sortedRoleIds = [...roleIds].sort((a, b) => a - b);
  const primaryRole = roleMap[sortedRoleIds[0]] || 'user';
  return primaryRole;
};

const ProtectedRoute = ({ children }) => {
  useEffect(() => {
    // Initialize Sentry user context when component mounts
    authService.initializeSentryContext();
    
    // Add breadcrumb for route access
    addBreadcrumb("Protected route accessed", "navigation", { 
      route: window.location.pathname 
    });
    
    logger.info("Protected route accessed", { route: window.location.pathname });
  }, []);

  // Check if user is authenticated
  if (!authService.isAuthenticated()) {
    addBreadcrumb("Access denied - not authenticated", "auth", { 
      route: window.location.pathname 
    });
    logger.warn("Access denied - user not authenticated", { route: window.location.pathname });
    
    // Redirect to login page if not authenticated
    return <Navigate to="/login" replace />;
  }

  // If authenticated, render the protected content
  // Check if children is a function (render prop pattern)
  if (typeof children === 'function') {
    const jwtUser = authService.getUser();
    
    // Transform JWT user object to match expected format
    const currentUser = {
      id: jwtUser.id,
      email: jwtUser.email,
      firstName: jwtUser.firstName,
      lastName: jwtUser.lastName,
      preferredName: jwtUser.preferredName,
      // Map role IDs to role names for backward compatibility
      role: mapRoleIdsToRoleName(jwtUser.roleIds),
      roleIds: jwtUser.roleIds,
      householdIds: jwtUser.householdIds
    };
    
    // Set Sentry tags for user context
    setTag("user.role", currentUser.role);
    setTag("user.has_households", currentUser.householdIds && currentUser.householdIds.length > 0);
    
    addBreadcrumb("User accessing protected route", "auth", { 
      userId: currentUser.id,
      userRole: currentUser.role,
      route: window.location.pathname
    });
    
    logger.info("User accessing protected route", { 
      userId: currentUser.id,
      userRole: currentUser.role,
      route: window.location.pathname
    });
    
    return children({ 
      onLogout: () => authService.logout(),
      currentUser: currentUser
    });
  }
  
  // Otherwise, render children as regular elements
  return children;
};

export default ProtectedRoute; 